import { AlertsCommentary } from '@aloft/toolkit-shared';

/**
 * This is a stand-in for the dot character in the storage key.
 * It is used to allow the use of the dot character in the storage key.
 */
const RHF_DOT_STANDIN = '/';

export const toAlertsCommentaryFormName = (storageKey: string): string =>
  storageKey.split('.').join(RHF_DOT_STANDIN);

export const fromAlertsCommentaryFormName = (formName: string): string =>
  formName.split(RHF_DOT_STANDIN).join('.');

export const toAlertsCommentaryFormValues = (
  storageValues: AlertsCommentary | undefined,
): AlertsCommentary => {
  if (!storageValues) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(storageValues).map(([key, value]) => [toAlertsCommentaryFormName(key), value]),
  );
};

export const trimAlertCommentaryFormValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

export const finalizeAlertCommentaryValue = (
  alertsCommentary: AlertsCommentary | undefined,
  alertCommentaryKey: string,
  value: unknown,
): AlertsCommentary | undefined => {
  const currentComment = trimAlertCommentaryFormValue(value);
  const nextAlertsCommentary = { ...(alertsCommentary ?? {}) };

  if (currentComment === undefined) {
    delete nextAlertsCommentary[alertCommentaryKey];
  } else {
    nextAlertsCommentary[alertCommentaryKey] = currentComment;
  }

  return Object.keys(nextAlertsCommentary).length ? nextAlertsCommentary : undefined;
};

export const mergeAlertsCommentaryFromFormKeys = (
  existingAlerts: AlertsCommentary,
  formKeys: readonly string[],
  getFormValue: (formName: string) => unknown,
): AlertsCommentary | undefined => {
  const merged: AlertsCommentary = { ...existingAlerts };

  for (const storageKey of formKeys) {
    const formName = toAlertsCommentaryFormName(storageKey);
    const raw = getFormValue(formName);
    if (raw === undefined) {
      continue;
    }
    const trimmed = trimAlertCommentaryFormValue(raw);
    if (trimmed === undefined) {
      delete merged[storageKey];
    } else {
      merged[storageKey] = trimmed;
    }
  }

  return Object.keys(merged).length > 0 ? merged : undefined;
};
